import { useState, useEffect } from 'react';
import { Car, Building2, Users, UserMinus, Wrench } from 'lucide-react';
import { api, errMsg, PREP, resolveConfirmedDuplicate } from './api';
import { AgencyForm, MutualForm } from './AgencyMutualForms';
import { RetenueForm } from './RetenueForm';
import { SavForm } from './SavForm';

const TABS = [['vehicule', 'A', 'Frais véhicule', Car], ['agence', 'B', "Frais d'agence", Building2], ['mutualise', 'C', 'Mutualisé', Users], ['retenue', 'D', 'Retenue commercial', UserMinus], ['sav', 'E', 'SAV', Wrench]];

export async function assignWithChecks(id, body, confirm) {
    let b = body;
    for (let i = 0; i < 4; i += 1) {
        try { return await api.assign(id, b); } catch (e) {
            if (e?.response?.status !== 409) throw e;
            const [msg, chargeId] = errMsg(e).split('|');
            if (msg.startsWith('Doublon confirmé')) {
                if (b.forcer_doublon) throw e;
                const fb = { ...b, forcer_doublon: true };
                return resolveConfirmedDuplicate(id, e, confirm, () => assignWithChecks(id, fb, confirm));
            } else if (msg.startsWith('Mois déjà payé')) {
                if (!(await confirm(msg, { title: 'Mois de commission déjà payé', confirmLabel: 'Confirmer la régularisation', danger: true }))) return false;
                b = { ...b, confirmer_mois_paye: true };
            } else if (msg.startsWith('Ligne identique')) {
                if (!(await confirm(`${msg}\n\nCette ligne semble déjà comptabilisée. La valider quand même ?`, { title: 'Doublon dans le même document', confirmLabel: 'Valider quand même', danger: true }))) return false;
                b = { ...b, force_meme_document: true };
            } else if (chargeId) {
                if (!(await confirm(`${msg}\n\nRattacher ce SAV à la charge existante (aucun nouveau coût) ?`, { title: 'Dépense déjà enregistrée', confirmLabel: 'Utiliser la charge existante' }))) return false;
                b = { ...b, use_charge_id: chargeId };
            } else if (b.type === 'frais_agence') {
                if (!(await confirm(`${msg}\n\nCréer quand même une seconde charge ?`, { title: 'Doublon probable', confirmLabel: 'Créer quand même', danger: true }))) return false;
                b = { ...b, force: true };
            } else throw e;
        }
    }
    return false;
}

export function AssignPanel({ d, vehicle, plateProps, busy, run, persist, confirm, onSplit, vehicleTab }) {
    const [tab, setTab] = useState(d.preparation?.body?.type === 'sav' ? 'sav' : d.destination_suggeree === 'frais_agence' ? 'agence' : d.destination_suggeree === 'frais_mutualise' ? 'mutualise' : 'vehicule');
    const [opts, setOpts] = useState(null);
    useEffect(() => { api.options(d.id).then(setOpts).catch(() => {}); }, [d.id]);
    const submit = async (body, message) => {
        if (!(await confirm(message, { title: PREP.on ? 'Préparer l\'affectation' : 'Confirmer l\'affectation', confirmLabel: PREP.on ? 'Envoyer pour validation' : 'Valider' }))) return;
        run(async () => {
            await persist();
            return PREP.on ? api.prepare(d.id, 'affecter', body, message) : assignWithChecks(d.id, body, confirm);
        }, PREP.on ? 'Préparation envoyée — un gérant la validera' : 'Affectation enregistrée');
    };
    const prepare = (body, message) => run(async () => { await persist(); return api.prepare(d.id, 'affecter', body, message); }, 'Préparation enregistrée — un gérant la validera');
    return (
        <div className="rounded-xl border border-gray-200" data-testid="assign-panel">
            <div className="grid grid-cols-5 border-b border-gray-100">
                {TABS.map(([k, letter, label, I]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`assign-tab-${k}`}
                        className={`flex flex-col items-center gap-0.5 py-2 text-[10px] sm:text-[11px] font-medium transition-colors ${tab === k ? 'text-[#6366F1] bg-indigo-50/60 border-b-2 border-[#6366F1]' : 'text-gray-500 hover:text-gray-800'}`}>
                        <I className="w-4 h-4" /><span>{letter} · {label}</span>
                    </button>
                ))}
            </div>
            <div className="p-3">
                {tab === 'vehicule' && vehicleTab}
                {tab !== 'vehicule' && !opts && <p className="text-[12px] text-gray-400">Chargement…</p>}
                {tab === 'agence' && opts && <AgencyForm d={d} opts={opts} busy={busy} submit={submit} />}
                {tab === 'mutualise' && opts && <MutualForm d={d} opts={opts} busy={busy} submit={submit} onSplit={onSplit} />}
                {tab === 'retenue' && opts && <RetenueForm d={d} opts={opts} vehicle={vehicle} busy={busy} submit={submit} />}
                {tab === 'sav' && opts && <SavForm d={d} opts={opts} vehicle={vehicle} plateProps={plateProps} busy={busy} submit={submit} prepare={prepare} />}
            </div>
        </div>
    );
}

export function InvoiceHistory({ id }) {
    const [h, setH] = useState([]);
    useEffect(() => { api.history(id).then(setH).catch(() => setH([])); }, [id]);
    if (!h.length) return null;
    return (
        <div data-testid="invoice-history">
            <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">Historique</p>
            <ul className="space-y-1">{h.map((e, i) => <li key={i} className="text-[11px] text-gray-600"><span className="text-gray-400">{new Date(e.timestamp).toLocaleString('fr-FR')} · {e.user_name}</span> — {e.comment || e.action}</li>)}</ul>
        </div>
    );
}
